/**
 * Simplified Chatbot Types
 * Focused on request/response interactions with long response times
 */
import type { ExecutiveCard, ExecutiveNotice, ExecutiveSteerAck } from '../../apis/executiveContract';

// Message types
export enum MessageType {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system', // For error messages, notifications
  TOOL = 'tool',     // For tool outputs
}

// Message status during sending/receiving
export enum MessageStatus {
  PENDING = 'pending',     // Being sent
  SENT = 'sent',          // Successfully sent
  FAILED = 'failed',      // Failed to send
  RETRYING = 'retrying',  // Being retried
}

// Response processing stages
export enum ResponseStage {
  IDLE = 'idle',           // Default state, no active response
  WAITING = 'waiting',     // Waiting for response to start
  STREAMING = 'streaming', // Actively receiving streamed content
  TOOL_CALLING = 'tool_calling', // Tool is being executed
  ERROR = 'error',         // Error occurred
}

// Chatbot UI states
export enum ChatbotState {
  CLOSED = 'closed',
  OPENING = 'opening',
  OPEN = 'open',
  COLLAPSED = 'collapsed',
  CLOSING = 'closing'
}

// Structured content for approval requests and questionnaires
export interface StructuredContent {
  response?: string;              // Main message text
  message?: string;               // Alternative message field
  text?: string;                  // Alternative text field
  answer?: string;                // Answer field from new API
  requires_approval?: boolean;    // Whether this message needs approval
  approval_data?: ApprovalData;   // Full approval details
  is_task_delegated?: boolean;    // Whether task is already delegated
  request_type?: string;          // Type of request
  request_text?: string;          // Additional request info
  requires_questionnaire?: boolean; // Whether this message has a questionnaire
  questionnaire_data?: QuestionnaireData; // Questionnaire questions
  questionnaire_completed?: boolean; // Whether questionnaire was submitted
  questionnaire_answers?: QuestionnaireAnswer[]; // Submitted answers (for display)
  // Byte offset into thinkingContent marking where the questionnaire sits in
  // chronological order: pre-questionnaire tool calls live at [0, marker_pos),
  // the wizard renders at marker_pos, post-answer tool calls live at
  // [marker_pos, end). Set once when the questionnaire arrives; never updated
  // afterwards (later content always appends to the end of thinkingContent).
  questionnaire_marker_position?: number;
}

// Approval data interfaces
export interface ApprovalData {
  company_name: string;
  conversation_id: string;
  message?: string;
  type: "write_tool_approval";
  pending_tools: PendingTool[];
}

export interface PendingTool {
  tool_name: string;           // e.g., "delegate_task_to_experts"
  tool_call_id: string;        // UUID for tracking
  approval_text: string;       // Human-readable description (can be 500+ chars)
  tool_args?: {                // Optional additional parameters
    client_requirements_note?: string;
    [key: string]: any;
  };
}

// Structured questionnaire types
export interface QuestionnaireQuestion {
  id: string;                  // Unique question identifier (e.g., "q1")
  prompt: string;              // The question text
  options: string[];           // 2-5 preset answer options
  allow_freeform: boolean;     // Whether to show an "Other" text input
  multi_select?: boolean;      // If true, user can select multiple options before submitting
  // Optional nudge tagging — when present, renders NudgeCard above the question
  nudge_id?: string;
  nudge_type?: 'todo' | 'question' | 'update';
  nudge_title?: string;
  nudge_severity?: 'critical' | 'warning' | 'info';
}

export interface QuestionnaireAnswer {
  question_id: string;         // Matches QuestionnaireQuestion.id
  selected_option: string | null;    // Single-select: the preset option chosen
  selected_options?: string[] | null; // Multi-select: all options chosen
  freeform_text: string | null;      // Custom text if "Other" was chosen
}

export interface QuestionnaireData {
  questions: QuestionnaireQuestion[];
  conversation_id: string;
}

/**
 * A message sent mid-turn (see midTurnInbox.ts). `sending`: POST in flight. `pending`: queued, not yet read.
 * `read`: a turn read it. `queued`: an executive steer waiting in the workflow for the next activation.
 */
export type InboxStatus = 'sending' | 'pending' | 'read' | 'stored' | 'queued' | 'uncertain' | 'failed';

export interface InboxItem {
  /** Local identity, stable from the moment the person presses Enter. */
  key: string;
  /** The server's id, once the POST is accepted. */
  id?: string;
  message: string;
  status: InboxStatus;
  /** Durable receipt: queue disappearance alone is never completion evidence. */
  durableReceipt?: boolean;
}

// Simple message interface
export interface ChatbotMessage {
  id: string;
  turnId?: string; // Durable stream identity, including consecutive background replies
  msgIds?: string[]; // Executive steers included in this saved message (possibly coalesced)
  sendIds?: string[]; // Client send identities, available before the RPC acknowledgment
  pendingHistory?: boolean; // Receipt bubble retained until the saved message arrives
  type: MessageType;
  content: string | StructuredContent; // Support both string and structured content
  timestamp: number;
  savedTimestamp?: string; // Server ISO timestamp of a saved message: the stable key annotations attach to
  status: MessageStatus;
  retryCount?: number;
  isDelegationApproved?: boolean; // Track approval state
  toolsUsed?: string[]; // Tools that were used in this message
  toolMarkers?: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }>; // Tool markers at specific positions in text (null = in progress, true = success, false = error)
  suggestedResponses?: string[]; // Suggested follow-up responses from the agent
  thinkingContent?: string; // Pre-conclusion content (tool calls, reasoning) shown in collapsible section
  images?: UploadedImage[]; // Support for uploaded images
  hidden?: boolean; // Hide the model payload; a non-empty display override can safely substitute for it
  display?: string; // User-facing substitute used in the bubble and title instead of model-only content
  harness?: boolean; // Written by the harness, not the person (a wake, a returned run): never a user bubble
  inboxStatus?: InboxStatus; // Sent while a turn ran: the bubble says whether Leo has read it
  executiveCard?: ExecutiveCard; // Executive harness card (interrupted, triggered, refused spawn, model failover)
  // Tool fields
  tool_name?: string;
  tool_args?: Record<string, any>;
  tool_output?: string;
  is_error?: boolean;
  tool_call_id?: string;
}

// Message capacity tracking
export interface MessageCapacity {
  tokenCount: number; // Total tokens in conversation
  capacityPercentage: number; // 0-100, based on token count
  maxTokens: number; // Maximum tokens for 100% capacity (200,000)
  isAtLimit: boolean; // true when capacity >= 100% or token count >= max
}

// Continuation state for context overflow recovery
export interface ContinuationState {
  isRecovering: boolean; // True while generating summary (~35 seconds)
  originalConversationId: string | null; // Conversation that overflowed
  retryMessage: string | null; // Message to retry after continuation
  recoveryStartTime: number | null; // When recovery started (for timeout tracking)
}

// `auth` is a request the server refused before any turn existed — usually a
// session whose auth cookie is gone. It needs its own phase because Retry
// re-sends the same request with the same missing cookie and fails identically;
// only a page refresh re-authenticates.
export type StreamInterruptionSource = 'network' | 'llm' | 'auth' | 'unknown';
export type StreamInterruptionPhase =
  | 'idle'
  | 'silent'
  | 'toast'
  | 'resume'
  | 'retry'
  | 'blocked'
  | 'session_expired';
export type InterruptAction = 'resume' | 'retry' | 'refresh' | 'none';

// Stream interrupted state for retry/resume handling
export interface StreamInterruptedState {
  turnId?: string; // A continuation can reconnect but cannot become a new user send
  isInterrupted: boolean; // True when stream was interrupted mid-response
  partialResponse: string; // Partial response user already saw
  error: string; // Error message from backend
  isRetryable: boolean; // Whether the error is retryable
  originalMessage: string; // Original user message to retry
  uploadedImages: UploadedImage[]; // Original uploaded images to preserve on retry/resume
  isHidden?: boolean; // Preserve model-only payload visibility across retry/resume
  displayText?: string; // Preserve the safe user-facing substitute across retry/resume
  interruptedAt: number | null; // Timestamp when interruption occurred
  conversationId: string | null; // Conversation ID for resume
  source: StreamInterruptionSource; // Network vs LLM interruption
  phase: StreamInterruptionPhase; // Single source of truth for UI phase
  disconnectStartedAt: number | null; // When network disconnect started
  lastReconnectAttemptAt: number | null; // Last reconnect attempt time
  sendId?: string; // The typed message this interruption belongs to; Retry keeps it
  // What a session-expiry Reconnect will do with the message: send it, put it
  // back in the composer, or reattach to the turn the server already accepted.
  sessionRecovery?: SessionRecovery;
}

export type SessionRecovery = 'replay' | 'restore' | 'reattach';

/** Delivery context an interruption carries into the store. */
export interface InterruptionDelivery {
  sendId?: string;
  /** The server declared the turn retryable with its checkpoint intact. */
  autoResume?: boolean;
}

/** What a session-expiry refusal knew about the send it refused. */
export interface SessionExpiryDelivery {
  sendId?: string;
  /** The server accepted the send before the refusal; replaying would send it twice. */
  acknowledged?: boolean;
  /** A POST of it may have reached the server; it comes back but does not send itself. */
  postMayHaveLanded?: boolean;
  /** The send would have created the conversation. */
  newConversation?: boolean;
}

// SSE connection state for tracking disconnections
export interface SSEConnectionState {
  turnId?: string;
  isConnected: boolean; // Current connection state
  disconnectedAt: number | null; // When connection was lost
  lastEventId: number; // Last event ID received (for resume)
  reconnectAttempts: number; // Number of reconnection attempts
}

// Uploaded image interface
export interface UploadedImage {
  file: File | null; // File object (null for loaded images)
  name?: string; // Asset name
  url?: string; // Presigned URL for display
  uploaded?: boolean; // Whether upload is complete
  s3_url?: string; // S3 URL after upload
  asset_id?: string; // Asset ID from backend
  s3_path_to_thumbnail?: string; // Thumbnail presigned URL
  description?: string; // Image description from Groq annotation
}

export type ExecutionTodoItemStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface ExecutionTodoItem {
  id: string;
  title: string;
  description?: string;
  status: ExecutionTodoItemStatus;
  order: number;
  note?: string;
  updated_at?: string;
}

export interface ExecutionTodo {
  title: string;
  version: number;
  status: 'active' | 'completed' | 'cancelled';
  source: string;
  created_at?: string;
  updated_at: string;
  active_item_id?: string | null;
  items: ExecutionTodoItem[];
  last_update?: {
    type?: string;
    note?: string;
    updated_at?: string;
    changes?: string[];
  };
}

// Response progress tracking
export interface ResponseProgress {
  requestId: string;
  turnId?: string;
  stage: ResponseStage;
  progress: number; // 0-100
  startTime: number;
  estimatedDuration: number; // in ms
  currentActivity: string;
  canCancel: boolean;
  streamedContent?: string; // For streaming updates
  // Node-gated answer isolation. The backend tags every streamed token with its
  // LangGraph node; only `conclusion_render` is user-facing. `conclusionContent`
  // is the render-node-only stream (the real reply); `nodeTagged` is true once any
  // token carried a node, signalling the stream is node-aware so the UI shows ONLY
  // the render output — never a draft reply a weak auxiliary model (Gemini Flash)
  // may emit in an earlier step (e.g. triage). Absent on the questionnaire-resume
  // stream (no node tags), where the UI falls back to tag-parsing the full stream.
  conclusionContent?: string;
  nodeTagged?: boolean;
  activeTools?: string[]; // Tools currently active
  toolMarkers?: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }>; // Tool markers at specific positions in text (null = in progress, true = success, false = error)
  messageCapacity?: MessageCapacity; // Message capacity info
  isStreamReset?: boolean; // When true, the store must clear ALL local streaming state (worker death recovery)
  isReplyReset?: boolean; // A model call is being tried again: the answer so far is taken back, the steps stay
  activityNote?: string; // The harness's word on the turn (a quiet model, a hand-off): the pill's activity line until the next step
  executionTodo?: ExecutionTodo | null; // Live execution todo snapshot
}

// Error information
export interface ChatbotError {
  id: string;
  code: string;
  message: string;
  timestamp: number;
  retryable: boolean;
  retryAfter?: number; // ms
  originalRequest?: string;
}

// Chatbot settings
export interface ChatbotSettings {
  mockMode: boolean;
}

// Chat history interfaces
export interface ConversationHistoryItem {
  conversation_id: string;
  title: string;
  timestamp: number;
  last_activity_at?: number; // Epoch seconds of last message activity (for "last used" sorting)
  preview?: string; // First message preview
  started_by_user_name?: string; // Name of the user who started this conversation
  origin?: string; // "slack" or "mcp" for conversations started in Slack or the Claude (MCP) connector; absent for web
  masquerade?: boolean; // Staff had this chat under masquerade
}

// History list filters. Origin narrows to a surface ('ephemeral' is the
// internal-only Leo-testing surface); started-by matches a
// `started_by_user_name` exactly. 'all' is the default for both axes.
export type HistoryOriginFilter = 'all' | 'slack' | 'mcp' | 'ephemeral';
export type HistoryStartedByFilter = string;

// UI view states
export enum ChatbotView {
  CHAT = 'chat',
  HISTORY = 'history',
}

/** `graph_complete`'s `pending`: work the turn left that comes back into this chat. */
export interface TurnPending {
  continuation: boolean;
  /** Reads that outran the turn's deadline. */
  reads?: number;
  /** Code jobs and module operations the turn started. */
  jobs?: number;
  /** The wakes, each coming back as a turn `continuation_<id>`. */
  continuations?: string[];
}

// Main chatbot state
export interface ChatbotStoreState {
  // Core conversation data
  messages: ChatbotMessage[];
  // Sent while a Code Mode turn ran, not yet part of `messages` (see midTurnInbox.ts). NOT persisted.
  inbox: InboxItem[];
  // Executive conversations only: the last steer's ack, and a refusal shown as-is (no retry).
  executiveSteer: ExecutiveSteerAck | null;
  executiveNotice: ExecutiveNotice | null;
  conversationId: string | undefined;
  title: string | null;

  // UI state
  state: ChatbotState;
  // Side panel stretched to full width (session-only — excluded from persist)
  panelStretched: boolean;
  responseStage: ResponseStage;
  /**
   * When the server's `graph_complete` marker landed — i.e. when the answer
   * stopped arriving on screen. Distinct from `responseStage` settling to
   * IDLE, which happens only after the post-turn phase (grooming, post-turn
   * tools) finishes, routinely fifteen seconds later. Monotonic within a
   * session; consumers detect a new completion by the value changing.
   */
  answerCompletedAt: number | null;
  /**
   * The latest `graph_complete`'s `pending`, stamped with `answerCompletedAt`:
   * work that turn left that comes back into this chat as a woken turn.
   */
  answerPending: TurnPending | null;
  /**
   * True from the moment settleInbox takes back unread mid-turn messages to
   * send them on until that next turn has started: the ask is still going.
   */
  inboxResending: boolean;
  currentView: ChatbotView;
  // The artifact whose full-state detail view has taken over the chat pane (the
  // card's info button sets it; the view's X clears it). Per-chat like
  // currentView, so a detail opened in one chat doesn't leak into another.
  activeDetailRef: import('@apis/ArtifactStatusService').ArtifactStatusRef | null;
  inputValue: string;
  inputFocusRequestId: number;
  inputFocused: boolean;
  inputPlaceholderOverride: string | null;
  // One-shot ArtifactPicker "open request" posted by external entry points
  // (e.g. the Overview's "Tune" button, via openArtifactPicker). The composer
  // watches these, adopts them into its own local picker state, opens pre-
  // drilled, then clears them via closeArtifactPicker. Live drill state (what
  // the user navigates to inside the open picker) is NOT here — it lives in
  // ChatbotInput, which owns the picker.
  //   - `artifactPickerSection`: section to pre-drill into ('monitors',
  //     'reports', 'campaigns:meta', ...). null = home view / no request.
  //   - `artifactPickerRestrictTo`: when set, lock the picker to these artifact
  //     types (Tune uses ['monitor']).
  artifactPickerSection: string | null;
  artifactPickerRestrictTo: import('@customTypes/artifact').Artifact['type'][] | null;
  showWelcomeScreen: boolean;

  // Pending artifacts queued from outside the ChatbotContext (e.g. Report iterate)
  // Consumed by ChatbotContext and moved into chatArtifacts on render
  pendingArtifacts: import('@customTypes/artifact').Artifact[];

  // Response tracking
  currentResponse: ResponseProgress | null;
  activeTools: string[]; // Tools currently being used

  // Message capacity tracking
  messageCapacity: MessageCapacity | null;
  executionTodo: ExecutionTodo | null;

  // Continuation state for context overflow recovery
  continuation: ContinuationState;

  // Stream interrupted state for retry/resume
  streamInterrupted: StreamInterruptedState;

  // SSE connection state
  sseConnection: SSEConnectionState;

  // Chat history
  conversationHistory: ConversationHistoryItem[];
  historyLoading: boolean;
  historySearchText: string;
  // Origin/started-by filters for the history list. Kept in the store (not
  // local component state) so they survive navigating out of and back into the
  // history view, matching historySearchText.
  historyOriginFilter: HistoryOriginFilter;
  historyStartedByFilter: HistoryStartedByFilter;
  conversationLoading: boolean; // Loading state for loading a conversation by ID
  executiveHistoryNextCursor: string | null;
  executiveHistoryViewingOlder: boolean;
  executiveHistoryLoading: boolean;
  executiveHistoryLoadId: number;

  // Error handling
  error: ChatbotError | null;

  // Settings
  settings: ChatbotSettings;

  // Side modal state
  isAssetHubModalOpen: boolean;

  // Slack simulation toggle (internal users only). When enabled, the
  // streamMessage request carries a slack_simulation flag that the
  // backend uses to inject SLACK_CONTEXT triggers without a real Slack
  // channel — lets us exercise the Slack-flow prompt paradigms from
  // the web chat.
  slackSimulation: SlackSimulationState;

  // The account's agent architecture, delivered on the /me payload (code_mode_enabled).
  // When true, new conversations route to the code-execution agent for everyone on the
  // account. Set in Back Office → Agent Architecture. NOT persisted (re-derived from /me
  // each session, fail-closed); synced into the store by ChatbotInput from the auth context.
  codeModeEnabled: boolean;

  // Per-account Code Mode SIMPLE opt-in (/me code_mode_simple_enabled) — same as
  // codeModeEnabled but routes to the primitives-only variant. NOT persisted.
  codeModeSimpleEnabled: boolean;

  // Read-only ask-card marker. True only on a per-card instance store (a conv_memo_
  // reply, seeded by sharedConversationStores); drives the backend read-only
  // code-mode surface for THIS send. The global panel store leaves it false so a
  // "Continue in chat" continuation runs full Leo. NOT persisted.
  askCardReadOnly: boolean;
}

export interface SlackSimulationState {
  enabled: boolean;
  async: boolean;
}

// 'code' = full code mode (mounted expert repo); 'code_simple' = the harness-stripped
// variant (primitives only, no repo). Both route to the code-execution agent server-side.
export type AgentArchitecture = 'classic' | 'code' | 'code_simple';

export interface OpenChatbotOptions {
  initialMessage?: string;
  /** Hide initialMessage itself; displayText, when provided, remains visible as its safe substitute. */
  hideInitialMessage?: boolean;
  /** Short user-facing bubble/title while initialMessage remains the full model payload. */
  displayText?: string;
  prefillInput?: string;
  prefillArtifacts?: import('@customTypes/artifact').Artifact[];
  /**
   * Seed a display-only USER message into a fresh chat WITHOUT sending it — used
   * by the Reports "+" dropdown to drop a pre-run confirmation card on the user
   * side. The card's own Run button sends the real (grounded) turn; the seed is
   * never flushed to the backend (history is server-authoritative).
   */
  seedUserMessage?: string;
}

// Store actions
export interface ChatbotActions {
  // Lifecycle
  openChatbot: (options?: OpenChatbotOptions) => void;
  collapseChat: () => void;
  togglePanelStretched: () => void;
  setPanelStretched: (panelStretched: boolean) => void;

  // Messaging
  sendMessage: (
    content: string,
    images?: UploadedImage[],
    hidden?: boolean,
    display?: string,
    onAccepted?: () => void,
    /** Only a replay of a parked message passes this; a fresh send mints its own. */
    sendId?: string,
  ) => Promise<string | null | void>;
  retryMessage: (messageId: string) => Promise<void>;
  retryFromError: () => Promise<void>;
  cancelCurrentResponse: () => void | Promise<void>;
  clearConversation: () => void;

  // Input handling
  setInputValue: (value: string) => void;
  setInputFocused: (value: boolean) => void;
  setInputPlaceholderOverride: (value: string | null) => void;
  requestInputFocus: () => void;
  openArtifactPicker: (opts: { section?: string | null; restrictTo?: import('@customTypes/artifact').Artifact['type'][] | null }) => void;
  closeArtifactPicker: () => void;
  clearPendingArtifacts: () => void;

  // Progress tracking
  updateResponseProgress: (progress: Partial<ResponseProgress>) => void;
  setResponseStage: (stage: ResponseStage, activity?: string) => void;
  /** Stamp `answerCompletedAt` and `answerPending`. Called on the server's end-of-turn marker. */
  markAnswerComplete: (pending?: TurnPending | null) => void;

  // Conversation inbox (midTurnInbox.ts): what the person sends while a Code Mode turn runs
  /** Queue a message for the running turn; resolves to the server's refusal text, or null. */
  sendToInbox: (message: string, resendKey?: string) => Promise<string | null>;
  /** An `inbox_delivered` chunk: the running turn read these ids. */
  markInboxDelivered: (ids: string[]) => void;
  /** Adopt the server's pending list, on load or reattach. */
  refreshInbox: () => Promise<void>;
  /** Turn end: fold what Leo read into the thread; send on (or, when stopped, restore) what it did not. */
  settleInbox: (completed: boolean) => Promise<void>;
  setActiveTools: (tools: string[]) => void;
  setExecutionTodo: (todo: ExecutionTodo | null) => void;

  // Error handling
  setError: (error: ChatbotError | null) => void;
  clearError: () => void;

  // Settings
  updateSettings: (settings: Partial<ChatbotSettings>) => void;
  toggleMockMode: () => void;

  // Conversation management
  setConversationId: (id: string | undefined) => void;
  loadConversationFromStorage: () => void;
  initializeStore: () => Promise<void>;

  // Chat history management
  setCurrentView: (view: ChatbotView) => void;
  setActiveDetailRef: (ref: import('@apis/ArtifactStatusService').ArtifactStatusRef | null) => void;
  loadConversationHistory: () => Promise<void>;
  setHistorySearchText: (text: string) => void;
  setHistoryOriginFilter: (filter: HistoryOriginFilter) => void;
  setHistoryStartedByFilter: (filter: HistoryStartedByFilter) => void;
  /** keepLiveTail: a save that does not hold the reply just watched live keeps that reply. */
  loadConversationById: (conversationId: string, opts?: { background?: boolean; isCurrentScope?: () => boolean; keepLiveTail?: boolean; historyCursor?: string }) => Promise<void>;
  saveCurrentConversation: () => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;

  // Approval actions
  approveTask: (messageId: string) => Promise<void>;
  cancelApproval: (messageId: string) => void;

  // Questionnaire actions
  submitQuestionnaireAnswers: (messageId: string, answers: import('./types').QuestionnaireAnswer[]) => Promise<void>;

  // Side modal actions
  openAssetHubModal: () => void;
  closeAssetHubModal: () => void;

  // Slack simulation
  setSlackSimulation: (next: Partial<SlackSimulationState>) => void;

  // Per-account Code Mode opt-in synced from /me (auto-routes an enabled account).
  setCodeModeEnabled: (next: boolean) => void;

  // Per-account Code Mode SIMPLE opt-in synced from /me.
  setCodeModeSimpleEnabled: (next: boolean) => void;

  // Continuation actions for context overflow recovery
  handleOverflowRecoveryStarted: (originalConversationId: string) => void;
  handleContinuationComplete: (newConversationId: string, retryMessage: string, continuedFrom: string, continuationChain: string[]) => Promise<void>;
  handleOverflowError: (errorMessage: string) => void;
  clearContinuationState: () => void;

  // Stream interrupted actions for retry/resume
  handleStreamInterrupted: (
    partialResponse: string,
    error: string,
    isRetryable: boolean,
    originalMessage: string,
    uploadedImages?: UploadedImage[],
    isHidden?: boolean,
    displayText?: string,
    turnId?: string,
    delivery?: InterruptionDelivery,
  ) => void;
  handleSessionExpired: (
    originalMessage: string,
    uploadedImages?: UploadedImage[],
    isHidden?: boolean,
    displayText?: string,
    delivery?: SessionExpiryDelivery,
  ) => void;
  /** Send the message parked before a session-expiry reload, once. */
  replayParkedSend: () => Promise<void>;
  retryInterruptedMessage: () => Promise<void>;
  recoverParentContinuation: (turnId: string) => Promise<void>;
  resumeStream: () => Promise<void>;
  clearStreamInterruptedState: () => void;

  // Stream lifecycle
  syncStreamLifecycle: (opts?: { skipHydration?: boolean; isCurrentScope?: () => boolean; isExecutiveInFlight?: () => boolean }) => Promise<void>;
  attachToLiveStream: (turnId?: string, isCurrentScope?: () => boolean, isExecutiveInFlight?: () => boolean) => Promise<void>;
  detachFromStream: () => void;

  // SSE connection tracking
  updateSSEConnectionState: (state: Partial<SSEConnectionState>) => void;
  updateStreamInterruptedState: (state: Partial<StreamInterruptedState>) => void;
  /** `sendId`: the typed message whose stream dropped, kept for Resume and its Retry. */
  handleSSEDisconnect: (sendId?: string) => void;
  handleSSEReconnect: () => void;

  // Internal actions (for store use)
  addMessage: (message: Omit<ChatbotMessage, 'id' | 'timestamp'>) => void;
  updateMessage: (id: string, updates: Partial<ChatbotMessage>) => void;
  setState: (state: ChatbotState) => void;

  // Private helper methods
  _handleMockResponse: (content: string, requestId: string) => Promise<void>;
  _handleRealResponse: (content: string, requestId: string, conversationId?: string, uploadedImages?: any[], isRetry?: boolean, isHidden?: boolean, displayText?: string, attempt?: import('@services/chatbot/chatbotApi').DeliveryAttempt) => Promise<void>;
}

// Combined store type
export type ChatbotStore = ChatbotStoreState & ChatbotActions;

// API request/response types
export interface ChatbotRequest {
  message: string;
  requestId: string;
  timestamp: number;
  conversationId?: string;
  conversationContext?: string[];
}

export interface ChatbotResponse {
  message: string;
  requestId: string;
  timestamp: number;
  processingTime: number;
  conversationId?: string;
  error?: {
    code: string;
    message: string;
  };
}

// Progress update from server (future enhancement)
export interface ServerProgressUpdate {
  requestId: string;
  stage: ResponseStage;
  progress: number;
  estimatedTimeRemaining: number;
  currentActivity: string;
}

// Mock response configuration
export interface MockResponseConfig {
  delay: number; // base delay in ms
  variability: number; // random variation (0-1)
  stages: Array<{
    stage: ResponseStage;
    duration: number; // % of total time
    activity: string;
  }>;
}
